import { BUILD_STAMP } from '../config';
import type { SaveState } from '../persist/autosave';
import { IconFit, IconMinus, IconPlus } from './icons';

interface Props {
  readonly save: SaveState;
  readonly storageMode: 'indexeddb' | 'memory';
  readonly elementCount: number;
  readonly selectionCount: number;
  readonly peerCount: number;
  readonly syncAvailable: boolean;
  readonly announcement: string;
  readonly onShowDiagnostics: () => void;
  readonly zoom: number;
  readonly onZoom: (factor: number) => void;
  readonly onZoomReset: () => void;
  readonly onZoomToFit: () => void;
}

function saveLabel(save: SaveState): string {
  if (save.quotaBlocked) return 'Storage full';
  switch (save.status) {
    case 'idle':
      return 'Ready';
    case 'pending':
      return 'Unsaved';
    case 'saving':
      return 'Saving';
    case 'saved':
      return `Saved ${new Date(save.lastSavedAt ?? Date.now()).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })}`;
    case 'error':
      return 'Save failed';
  }
}

/**
 * Grouped cells rather than a row of equally weighted facts. Only what changes
 * gets emphasis, and anything that is merely reassuring stays quiet or is
 * omitted when it has nothing to say.
 */
export function StatusBar(props: Props) {
  const {
    save,
    storageMode,
    elementCount,
    selectionCount,
    peerCount,
    syncAvailable,
    announcement,
    onShowDiagnostics,
    zoom,
    onZoom,
    onZoomReset,
    onZoomToFit,
  } = props;

  return (
    <div className="statusbar">
      <span className="cell">
        <span className={`dot ${save.quotaBlocked ? 'error' : save.status}`} aria-hidden="true" />
        <span className={save.status === 'saved' || save.status === 'idle' ? undefined : 'strong'}>
          {saveLabel(save)}
        </span>
      </span>

      <span className="cell">
        <span className="strong">{elementCount}</span>{' '}
        {elementCount === 1 ? 'element' : 'elements'}
        {selectionCount > 0 ? (
          <>
            <span aria-hidden="true">&middot;</span>
            <span className="strong">{selectionCount}</span> selected
          </>
        ) : null}
      </span>

      <span
        className="cell"
        title={
          storageMode === 'indexeddb'
            ? 'Stored in this browser (IndexedDB). Nothing leaves this machine.'
            : 'Storage is unavailable, so changes will not be kept.'
        }
      >
        {storageMode === 'indexeddb' ? 'Local' : 'In memory only'}
      </span>

      {/* Only worth a cell when there is genuinely another window. */}
      {peerCount > 0 ? (
        <span className="cell">
          <span className="strong">{peerCount}</span>{' '}
          {peerCount === 1 ? 'other window' : 'other windows'}
        </span>
      ) : null}

      {!syncAvailable ? <span className="cell">No multi-window sync</span> : null}

      <span className="spacer" />

      <span className="cell zoom-bar" role="group" aria-label="Zoom">
        <button
          type="button"
          className="btn btn-icon"
          aria-label="Zoom out"
          onClick={() => onZoom(1 / 1.2)}
        >
          <IconMinus />
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm value"
          aria-label={`Zoom is ${Math.round(zoom * 100)} percent. Reset to 100 percent.`}
          onClick={onZoomReset}
        >
          {Math.round(zoom * 100)}%
        </button>
        <button type="button" className="btn btn-icon" aria-label="Zoom in" onClick={() => onZoom(1.2)}>
          <IconPlus />
        </button>
        <button
          type="button"
          className="btn btn-icon"
          aria-label="Zoom to fit the whole board"
          onClick={onZoomToFit}
        >
          <IconFit />
        </button>
      </span>

      <span className="cell">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onShowDiagnostics}
          title={`Built ${new Date(BUILD_STAMP).toLocaleString()}. Open diagnostics.`}
        >
          {new Date(BUILD_STAMP).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
        </button>
      </span>

      {/* Announcements for screen readers: selection, saves and failures. */}
      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
